import { useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchFoodProducts,
  fetchFoodRetailManualReviewQueue,
  fetchFoodRetailManualReviewSummary,
  postFoodRetailManualMatch,
  postFoodRetailManualReject,
} from "../../../../api/foodApi";
import { FOOD_SOURCE_KEYS } from "../../../../constants/foodSourceConstants";
import AdminTableContainer from "../layout/AdminTableContainer";

const BASKET_STAPLE_ORDER = [
  "Rice",
  "Eggs",
  "Milk",
  "Arepa Flour",
  "Cooking Oil",
  "Beans",
  "Lentils",
  "Chicken",
  "Ground Beef",
  "Potato",
  "Tomato",
  "Onion",
  "Banana",
  "Coffee",
];

function formatCop(value) {
  if (value == null) return "—";
  return `COP ${Math.round(Number(value)).toLocaleString("es-CO")}`;
}

function confidenceLabel(confidence) {
  if (confidence == null) return "—";
  return `${Math.round(Number(confidence) * 100)}%`;
}

const FoodNormalizationReviewPage = ({ onClose }) => {
  const [sourceKey, setSourceKey] = useState(FOOD_SOURCE_KEYS.ARA_COLOMBIA);
  const [statusFilter, setStatusFilter] = useState("");
  const [likelyStapleOnly, setLikelyStapleOnly] = useState(true);
  const [queue, setQueue] = useState([]);
  const [summary, setSummary] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [selectedProductId, setSelectedProductId] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");

  const stapleProducts = useMemo(() => {
    const byName = new Map(
      products.map((p) => [p.normalizedName || p.displayName, p])
    );
    const ordered = BASKET_STAPLE_ORDER.map((name) => byName.get(name)).filter(Boolean);
    const rest = products.filter(
      (p) =>
        (p.isStaple || p.basketEligible) &&
        !BASKET_STAPLE_ORDER.includes(p.normalizedName)
    );
    return [...ordered, ...rest];
  }, [products]);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage("");
      const [queueData, summaryData, productData] = await Promise.all([
        fetchFoodRetailManualReviewQueue({
          sourceKey,
          status: statusFilter || undefined,
          likelyStapleOnly,
          limit: 100,
        }),
        fetchFoodRetailManualReviewSummary({ sourceKey }),
        fetchFoodProducts(),
      ]);
      setQueue(queueData.items || []);
      setSummary(summaryData);
      setProducts(productData.products || []);
    } catch (error) {
      setQueue([]);
      setErrorMessage(error.message || "Unable to load manual review queue.");
    } finally {
      setLoading(false);
    }
  }, [sourceKey, statusFilter, likelyStapleOnly]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const selectedRow = useMemo(
    () => queue.find((row) => row.observationId === selectedId) || null,
    [queue, selectedId]
  );

  useEffect(() => {
    if (!selectedRow) {
      setSelectedProductId("");
      setReviewNotes("");
      return;
    }
    setSelectedProductId(
      String(selectedRow.suggestedProductId || selectedRow.possibleCanonicalProducts?.[0]?.productId || "")
    );
    setReviewNotes("");
  }, [selectedRow]);

  const handleMatch = async (observationId, productId, notes) => {
    const product = products.find((p) => p.id === productId);
    if (!product) return;
    try {
      setActing(true);
      setErrorMessage("");
      await postFoodRetailManualMatch({
        sourceKey,
        observationId,
        productId,
        reviewedBy: "admin",
        notes: notes || `Manual match to ${product.normalizedName || product.displayName}`,
      });
      await loadData();
      setSelectedId(null);
    } catch (error) {
      setErrorMessage(error.message || "Manual match failed.");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async (observationId, reason) => {
    try {
      setActing(true);
      setErrorMessage("");
      await postFoodRetailManualReject({
        sourceKey,
        observationId,
        reviewedBy: "admin",
        reason,
      });
      await loadData();
      setSelectedId(null);
    } catch (error) {
      setErrorMessage(error.message || "Reject failed.");
    } finally {
      setActing(false);
    }
  };

  const handleMatchSuggested = () => {
    if (!selectedRow?.suggestedProductId) return;
    handleMatch(selectedRow.observationId, selectedRow.suggestedProductId, reviewNotes);
  };

  return (
    <div className="admin-page food-normalization-page food-normalization-review-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Food Data · Retail Ingestion</p>
          <h1>ARA Product Normalization Review</h1>
          <p className="muted">
            Manually resolve ambiguous ARA observations for the Basic Colombian Household Basket.
          </p>
        </div>
        {onClose ? (
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
        ) : null}
      </header>

      <section className="panel-card food-normalization-review-page__guidance">
        <h2>Review guidance</h2>
        <p>
          Only manually match products that clearly represent a Basic Basket staple. Leave
          non-staples as NO_MATCH. Do not use substitutions yet unless explicitly supported.
        </p>
        <ul className="muted">
          <li>Match “Huevos AA x30” → Eggs</li>
          <li>Match “Arroz Diana 1 kg” → Rice</li>
          <li>Reject “Leche condensada” for Milk basket</li>
          <li>Reject “Galletas café” for Coffee basket</li>
          <li>Reject “Papas fritas” for Potato basket</li>
          <li>Reject household goods and non-food promos</li>
        </ul>
      </section>

      {summary ? (
        <section className="panel-card">
          <h2>Review summary</h2>
          <dl className="food-retail-ingestion-page__readiness-grid">
            <dt>Needs review</dt>
            <dd>{summary.summary?.needsReview ?? "—"}</dd>
            <dt>Low confidence</dt>
            <dd>{summary.summary?.lowConfidence ?? "—"}</dd>
            <dt>Manual matched</dt>
            <dd>{summary.summary?.manualMatched ?? "—"}</dd>
            <dt>Rejected</dt>
            <dd>{summary.summary?.rejected ?? "—"}</dd>
            <dt>Staples with match</dt>
            <dd>
              {summary.summary?.basketStaplesWithMatch ?? "—"} /{" "}
              {BASKET_STAPLE_ORDER.length}
            </dd>
            <dt>Still missing staples</dt>
            <dd>{summary.summary?.basketStaplesStillMissing ?? "—"}</dd>
          </dl>
        </section>
      ) : null}

      <section className="panel-card food-normalization-page__controls">
        <div className="food-normalization-page__control-row">
          <label>
            Source
            <select value={sourceKey} onChange={(e) => setSourceKey(e.target.value)}>
              <option value={FOOD_SOURCE_KEYS.ARA_COLOMBIA}>{FOOD_SOURCE_KEYS.ARA_COLOMBIA}</option>
            </select>
          </label>
          <label>
            Status filter
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All reviewable</option>
              <option value="NEEDS_REVIEW">NEEDS_REVIEW</option>
              <option value="LOW_CONFIDENCE">LOW_CONFIDENCE</option>
              <option value="NO_MATCH">NO_MATCH</option>
              <option value="PENDING">PENDING</option>
            </select>
          </label>
          <label className="food-normalization-page__checkbox">
            <input
              type="checkbox"
              checked={likelyStapleOnly}
              onChange={(e) => setLikelyStapleOnly(e.target.checked)}
            />
            Likely staple only
          </label>
          <button type="button" className="btn btn--ghost" onClick={loadData} disabled={loading}>
            Refresh
          </button>
        </div>
        {errorMessage ? <p className="food-dashboard__error">{errorMessage}</p> : null}
      </section>

      <section className="panel-card">
        {loading ? <p className="muted">Loading review queue…</p> : null}
        <AdminTableContainer>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Raw product</th>
                <th>Brand</th>
                <th>Price</th>
                <th>Package</th>
                <th>Suggested</th>
                <th>Confidence</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {queue.map((row) => (
                <tr key={row.observationId}>
                  <td>{row.rawProductName}</td>
                  <td>{row.brand || "—"}</td>
                  <td>{formatCop(row.priceCop)}</td>
                  <td>
                    {row.packageQuantity != null
                      ? `${row.packageQuantity} ${row.packageUnit || ""}`
                      : "—"}
                  </td>
                  <td>{row.suggestedProductName || "—"}</td>
                  <td>{confidenceLabel(row.suggestedConfidence)}</td>
                  <td>{row.productMatchStatus}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={() => setSelectedId(row.observationId)}
                    >
                      Review
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTableContainer>
      </section>

      {selectedRow ? (
        <section className="panel-card food-normalization-page__review">
          <h2>Review observation</h2>
          <p>
            <strong>Raw:</strong> {selectedRow.rawProductName}
          </p>
          <p>
            <strong>Reason:</strong> {selectedRow.productMatchReason || "—"}
          </p>
          {selectedRow.sourceUrl ? (
            <p>
              <a href={selectedRow.sourceUrl} target="_blank" rel="noreferrer">
                Source URL
              </a>
            </p>
          ) : null}
          <label>
            Notes
            <input
              type="text"
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
              placeholder="Optional review note"
            />
          </label>
          <label>
            Canonical product
            <select
              aria-label="Canonical product"
              value={selectedProductId}
              onChange={(e) => setSelectedProductId(e.target.value)}
            >
              <option value="">Select staple…</option>
              {stapleProducts.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.normalizedName || product.displayName}
                </option>
              ))}
            </select>
          </label>
          <div className="food-normalization-page__manual-actions">
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={acting || !selectedRow.suggestedProductId}
              onClick={handleMatchSuggested}
            >
              Match to suggested
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={acting || !selectedProductId}
              onClick={() =>
                handleMatch(selectedRow.observationId, selectedProductId, reviewNotes)
              }
            >
              Match to selected
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={acting}
              onClick={() =>
                handleReject(
                  selectedRow.observationId,
                  reviewNotes || "Rejected — non-staple or invalid basket match"
                )
              }
            >
              Reject / NO_MATCH
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              disabled={acting}
              onClick={() => setSelectedId(null)}
            >
              Skip for now
            </button>
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default FoodNormalizationReviewPage;
